import { api } from "./authService";

const ensurePdfFilename = (rawFilename) => {
  const fallback = "Semesterreview.pdf";
  if (!rawFilename || typeof rawFilename !== "string") {
    return fallback;
  }

  let filename = rawFilename.trim();
  filename = filename.replace(/^['"]+|['"]+$/g, "");
  filename = filename.split(";")[0].trim();
  filename = filename.replace(/[^\w.\-() ]+$/g, "");

  if (!filename.toLowerCase().endsWith(".pdf")) {
    filename = filename.replace(/\.+$/, "");
    filename = `${filename || "Semesterreview"}.pdf`;
  }

  return filename;
};

const semesterReviewService = {
  // Get learners for selection (filtered by profession)
  getLearnersForSelection: async () => {
    try {
      const response = await api.get("/semester-reviews/learners");
      return { success: true, users: response.data }; // Match expected structure if possible, or just return data
    } catch (error) {
      console.error("Error fetching learners for selection:", error);
      throw error;
    }
  },

  // Get all semester review templates
  getTemplates: async () => {
    try {
      const response = await api.get("/semester-reviews/templates");
      return response.data;
    } catch (error) {
      console.error("Error fetching semester review templates:", error);
      throw error;
    }
  },

  // Get template by code
  getTemplateByCode: async (code) => {
    try {
      const response = await api.get(`/semester-reviews/templates/${code}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching template ${code}:`, error);
      throw error;
    }
  },

  // Get semester reviews for a student
  getReviewsByStudent: async (studentId, semesterId = null) => {
    try {
      const params = semesterId ? { semesterId } : {};
      const response = await api.get(`/semester-reviews/student/${studentId}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching reviews for student ${studentId}:`, error);
      throw error;
    }
  },

  // Get semester review by ID
  getReviewById: async (reviewId) => {
    try {
      const response = await api.get(`/semester-reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching review ${reviewId}:`, error);
      throw error;
    }
  },

  // Create a new semester review
  createReview: async (reviewData) => {
    try {
      const response = await api.post("/semester-reviews", reviewData);
      return response.data;
    } catch (error) {
      console.error("Error creating semester review:", error);
      throw error;
    }
  },

  // Update semester review
  updateReview: async (reviewId, reviewData) => {
    try {
      const response = await api.put(`/semester-reviews/${reviewId}`, reviewData);
      return response.data;
    } catch (error) {
      console.error(`Error updating review ${reviewId}:`, error);
      throw error;
    }
  },

  // Submit semester review (finalize)
  submitReview: async (reviewId) => {
    try {
      const response = await api.post(`/semester-reviews/${reviewId}/submit`);
      return response.data;
    } catch (error) {
      console.error(`Error submitting review ${reviewId}:`, error);
      throw error;
    }
  },

  // Get reviews by trainer (Berufsbildner)
  getReviewsByTrainer: async (trainerId, semesterId = null) => {
    try {
      const params = semesterId ? { semesterId } : {};
      const response = await api.get(`/semester-reviews/trainer/${trainerId}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching reviews for trainer ${trainerId}:`, error);
      throw error;
    }
  },

  // Delete semester review
  deleteReview: async (reviewId) => {
    try {
      const response = await api.delete(`/semester-reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting review ${reviewId}:`, error);
      throw error;
    }
  },

  // Get semester options for creation dropdown
  getSemesterOptions: async (learnerId = null) => {
    try {
      const params = learnerId ? { learnerId } : {};
      const response = await api.get("/semester-reviews/semester-options", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching semester options:", error);
      throw error;
    }
  },

  // NEW APIs FOR ASSIGNMENT AND OVERVIEW

  // Get my semester reviews (learner perspective)
  getMyReviews: async () => {
    try {
      const response = await api.get("/semester-reviews/me");
      return response.data;
    } catch (error) {
      console.error("Error fetching my reviews:", error);
      throw error;
    }
  },

  // Get trainer reviews with filters
  getTrainerReviews: async (filters = {}) => {
    try {
      const params = {};
      if (filters.learnerId) params.learnerId = filters.learnerId;
      if (filters.semesterId) params.semesterId = filters.semesterId;
      if (filters.status) params.status = filters.status;
      
      const response = await api.get("/semester-reviews/trainer", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching trainer reviews:", error);
      throw error;
    }
  },

  // Update review details (e.g. title)
  updateReviewDetails: async (reviewId, data) => {
    try {
      const response = await api.patch(`/semester-reviews/${reviewId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating review details ${reviewId}:`, error);
      throw error;
    }
  },

  // Save answers for a review
  saveAnswers: async (reviewId, answers, totalCompetenceItems = null) => {
    try {
      const payload = { answers };
      if (totalCompetenceItems) {
        payload.totalCompetenceItems = totalCompetenceItems;
      }
      const response = await api.post(`/semester-reviews/${reviewId}/answers`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error saving answers for review ${reviewId}:`, error);
      throw error;
    }
  },

  // Update review status
  updateStatus: async (reviewId, status) => {
    try {
      const response = await api.patch(`/semester-reviews/${reviewId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating status for review ${reviewId}:`, error);
      throw error;
    }
  },

  // Mark review as read
  markAsRead: async (reviewId) => {
    try {
      const response = await api.patch(`/semester-reviews/${reviewId}/mark-read`);
      return response.data;
    } catch (error) {
      console.error(`Error marking review ${reviewId} as read:`, error);
      throw error;
    }
  },

/*
  // Delete review (admin only) - Duplicate key removed
  deleteReview: async (reviewId) => {
    try {
      const response = await api.delete(`/semester-reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting review ${reviewId}:`, error);
      throw error;
    }
  },
*/

  // Export review as PDF
  exportReviewAsPdf: async (reviewId) => {
    try {
      const { url, filename } = await semesterReviewService.getReviewPdfPreview(
        reviewId,
      );

      semesterReviewService.downloadPdfFromUrl(url, filename);
      return { success: true };
    } catch (error) {
      console.error(`Error exporting review ${reviewId} as PDF:`, error);
      throw error;
    }
  },

  getReviewPdfPreview: async (reviewId) => {
    try {
      const response = await api.get(`/semester-reviews/${reviewId}/export-pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const contentDisposition = response.headers['content-disposition'];
      let filename = `Semesterreview.pdf`;
      if (contentDisposition) {
        const utf8FilenameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        const simpleFilenameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);

        if (utf8FilenameMatch?.[1]) {
          filename = decodeURIComponent(utf8FilenameMatch[1]);
        } else if (simpleFilenameMatch?.[1]) {
          filename = simpleFilenameMatch[1];
        }
      }

      return { url, filename: ensurePdfFilename(filename) };
    } catch (error) {
      console.error(`Error loading preview for review ${reviewId}:`, error);
      throw error;
    }
  },

  downloadPdfFromUrl: (url, filename = "Semesterreview.pdf") => {
    const link = document.createElement('a');
    link.href = url;
    link.download = ensurePdfFilename(filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  revokeObjectUrl: (url) => {
    if (url) {
      window.URL.revokeObjectURL(url);
    }
  },

  getPreviousReviews: async (learnerId) => {
    try {
      const response = await api.get(`/semester-reviews/learners/${learnerId}/previous`);
      return response.data;
    } catch (error) {
      console.error("Error fetching previous review list:", error);
      throw error;
    }
  },

  getReviewPlaceholders: async (reviewId) => {
    try {
      const response = await api.get(`/semester-reviews/${reviewId}/placeholders`);
      return response.data;
    } catch (error) {
       console.error("Error fetching review placeholders:", error);
       throw error;
    }
  },

  getPreviousMeetings: async (learnerId, currentReviewId = null) => {
    try {
      const params = {};
      if (currentReviewId) {
        params.currentReviewId = currentReviewId;
      }
      const response = await api.get(`/semester-meetings/${learnerId}/previous`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching previous meetings:", error);
      throw error;
    }
  },

  getSmartGoal: async (meetingId) => {
    try {
      const response = await api.get(`/semester-meetings/${meetingId}/smart-goal`);
      return response.data; // Expected { smartGoal: "..." }
    } catch (error) {
      console.error("Error fetching smart goal:", error);
      throw error;
    }
  },

  getDeletedReviews: async () => {
    try {
      const response = await api.get("/semester-reviews/deleted");
      return response.data;
    } catch (error) {
      console.error("Error fetching deleted reviews:", error);
      throw error;
    }
  },

  restoreReview: async (reviewId) => {
    try {
      const response = await api.post(`/semester-reviews/${reviewId}/restore`);
      return response.data;
    } catch (error) {
      console.error(`Error restoring review ${reviewId}:`, error);
      throw error;
    }
  },

  // Downloads the selected (or, with no reviewIds, every visible) review as one ZIP,
  // each review kept as an individual PDF. Generation stays server-side so the browser
  // only performs one download and permissions remain centralized.
  exportLearnerReviewsAsZip: async (learnerId, reviewIds) => {
    try {
      const response = await api.get(`/semester-reviews/learners/${learnerId}/export-zip`, {
        responseType: "blob",
        params: reviewIds && reviewIds.length ? { reviewIds } : undefined,
        paramsSerializer: {
          indexes: null,
        },
      });

      const contentDisposition = response.headers["content-disposition"] || "";
      const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
      const simpleMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      const filename = utf8Match?.[1]
        ? decodeURIComponent(utf8Match[1])
        : simpleMatch?.[1] || "reports.zip";

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/zip" }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(`Error exporting reviews for learner ${learnerId} as zip:`, error);
      throw error;
    }
  },
};



export default semesterReviewService;
